'use client'

import React, { createContext, useContext, useState, ReactNode } from 'react'

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'

import { useDataProviderContext } from '@/shared/contexts/DataProvider'

// ── Local resource types ────────────────────────────────────────────────────────

export type RecordType = Record<string, unknown>

export type CustomAction =
  | string
  | {
      method: string
      name: string
    }

export interface DeleteOptions {
  path?: string
  confirm?: boolean
  confirmTitle?: string
  confirmText?: string
  callback?: (success: boolean, data?: any) => void
}

export type Config = {
  apiBasePath: string
  basePath?: string
  edit?: boolean
  options?: any
  forceIdPresence?: boolean
  shouldFetch?: boolean
  params?: Record<string, unknown>
  customAction?: string
  pagination?: { perPage?: number }
  sort?: { field: string; order: 'asc' | 'desc' } | { field: string; order: 'asc' | 'desc' }[]
  filter?: Record<string, unknown>
  humanName?: { singular: string; plural: string }
  icon?: string | React.ComponentType
}

// ── isFunction util ────────────────────────────────────────────────────────────

const isFunction = (value: any): value is (...args: any[]) => any =>
  typeof value === 'function'

// ── showErrorAlert util (inlined, uses dynamic import for Swal) ────────────────

const showErrorAlert = async (error: any): Promise<void> => {
  if (typeof window === 'undefined') {
    console.error('Error:', error)
    return
  }
  try {
    const { default: Swal } = await import('sweetalert2')
    const message =
      error?.response?.data?.message ||
      error?.message ||
      String(error)
    Swal.fire({ icon: 'error', title: 'Erro', html: message })
  } catch {
    console.error('Error:', error)
  }
}

// ── get / omit utils (inlined) ─────────────────────────────────────────────────

const get = (obj: any, path: string, defaultValue?: any): any => {
  const keys = path.split('.')
  let result: any = obj
  for (const key of keys) {
    result = result?.[key]
    if (result === undefined) return defaultValue
  }
  return result ?? defaultValue
}

const omit = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const newObj = { ...obj }
  keys.forEach(key => { delete newObj[key] })
  return newObj
}

// ── useRecord ──────────────────────────────────────────────────────────────────

const DEFAULT_QUERY_OPTIONS: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'> = {
  retry: false,
  retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000)
}

interface UseRecordProps<TParams = object> {
  r: string
  id?: string | number
  options?: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'>
  params?: TParams
  action?: string
  forceIdPresence?: boolean
  shouldFetch?: boolean
}

interface UseRecordReturn<TRecord = object, TErrors = object | []> {
  record?: TRecord | null
  errors?: TErrors
  errorStatusCode?: number
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  loadWrapperConfig: { isLoading: boolean; isError: boolean; isSuccess: boolean }
  refetch: () => void
  [key: string]: unknown
}

const useRecord = ({
  r: path,
  id,
  forceIdPresence = true,
  shouldFetch = true,
  options = DEFAULT_QUERY_OPTIONS,
  params,
  action
}: UseRecordProps): UseRecordReturn => {
  forceIdPresence = typeof forceIdPresence === 'undefined' || forceIdPresence
  shouldFetch = typeof shouldFetch === 'undefined' || shouldFetch

  const readyToFetch =
    !!shouldFetch &&
    !!path &&
    !path.includes('undefined') &&
    (!!id || !forceIdPresence)

  const { isLoading, isError, isSuccess, data, refetch, ...rest } = useQuery({
    enabled: !!readyToFetch,
    ...options,
    queryKey: [
      path,
      `${id ? id : ''}` + `${action ? '/' + action : ''}`,
      params || {}
    ]
  }) as UseQueryResult<UseRecordReturn>

  const record = data?.record
  const errors = data?.errors
  const errorStatusCode = get(rest, 'failureReason.response.status') as number | undefined
  const supInfo = omit((data || {}) as RecordType, ['record' as any, 'errors' as any])
  const loadWrapperConfig = { isLoading, isError, isSuccess }

  return {
    record,
    ...supInfo,
    ...rest,
    errors,
    errorStatusCode,
    isLoading,
    isError,
    isSuccess,
    loadWrapperConfig,
    refetch
  }
}

// ── useDeleteHandler ───────────────────────────────────────────────────────────

const useDeleteHandler = (apiBasePath: string) => {
  const { dataProvider } = useDataProviderContext()
  const queryClient = useQueryClient()

  const handleDelete = async (
    id: number | string,
    options?: DeleteOptions
  ): Promise<{ status: string }> => {
    let status: string
    const {
      confirm = true,
      confirmTitle = 'Tem certeza?',
      confirmText = 'O registro será excluído permanentemente!',
      path = apiBasePath,
      callback
    } = options ?? {}

    if (!path)
      throw new Error(`No apiBasePath provided for delete action with id '${id}'`)

    const deleteRecord = async (id: string | number) => {
      try {
        const res = await dataProvider.delete(path, id)
        queryClient.invalidateQueries({ queryKey: [apiBasePath] })
        const { default: Swal } = await import('sweetalert2')
        Swal.fire('Excluído!', 'Seu registro foi excluído permanentemente.', 'success')
        status = 'deleted'
        callback?.(true, res?.data)
      } catch (error) {
        status = 'error'
        showErrorAlert(error)
        callback?.(false, error)
      }
    }

    if (confirm) {
      const { default: Swal } = await import('sweetalert2')
      const { isConfirmed, dismiss } = await Swal.fire({
        title: confirmTitle,
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, pode excluir!',
        cancelButtonText: 'Não',
        reverseButtons: true
      })

      if (isConfirmed) {
        await deleteRecord(id)
      } else {
        status = 'canceled'
        if (dismiss === Swal.DismissReason.cancel) {
          Swal.fire('Cancelado', 'Seu registro está a salvo', 'error')
        }
      }
    } else {
      await deleteRecord(id)
    }

    return { status: status! }
  }

  return { handleDelete }
}

// ── ResourceContext ─────────────────────────────────────────────────────────────

type ResourceContextType<
  TRecord extends RecordType = RecordType,
  TConfig extends Config = Config
> = {
  id?: string
  record: TRecord | null
  errors: any
  customAction?: string
  config: TConfig

  isSubmitting: boolean
  isEdit: boolean
  isNew: boolean
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  loadWrapperConfig: {
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
  }

  setIsSubmitting: (value: boolean) => void
  setSavedRecord: (record: TRecord) => void
  handleDelete: (id: string | number, options?: DeleteOptions) => Promise<{ status: string }>
  refetch: () => void
}

type ResourceProviderProps<
  TRecord extends RecordType = RecordType,
  TConfig extends Config = Config
> = {
  id?: string
  config: TConfig
  children: ReactNode
} & Partial<ResourceContextType<TRecord, TConfig>>

const ResourceContext = createContext<ResourceContextType<any, any> | null>(null)

export const useResourceContext = <
  TRecord extends RecordType = RecordType,
  TConfig extends Config = Config
>(): ResourceContextType<TRecord, TConfig> => {
  const context = useContext(ResourceContext)
  if (!context) {
    throw new Error('useResourceContext must be used within a ResourceProvider')
  }
  return context as ResourceContextType<TRecord, TConfig>
}

export const ResourceProvider = <
  TRecord extends RecordType = RecordType,
  TConfig extends Config = Config
>({
  id,
  config,
  children
}: ResourceProviderProps<TRecord, TConfig>) => {
  const {
    apiBasePath,
    edit,
    options,
    forceIdPresence = true,
    shouldFetch = true,
    params,
    customAction
  } = config

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [savedRecord, setSavedRecord] = useState<TRecord | null>(null)

  const { handleDelete } = useDeleteHandler(apiBasePath)

  const isEdit = edit || !!id || !forceIdPresence

  const {
    record: loadedRecord,
    errors,
    isLoading,
    isError,
    isSuccess,
    loadWrapperConfig,
    refetch,
    ...recordRest
  } = useRecord({
    r: apiBasePath,
    id,
    options: {
      revalidateOnFocus: false,
      ...options
    },
    forceIdPresence,
    shouldFetch,
    params,
    action: customAction
  })

  const record = savedRecord ?? loadedRecord

  return (
    <ResourceContext.Provider
      value={{
        id,
        record,
        errors,

        isLoading,
        isError,
        isSuccess,
        loadWrapperConfig,
        isSubmitting,
        isEdit,
        isNew: !isEdit,

        config,

        setIsSubmitting,
        setSavedRecord,
        handleDelete,
        refetch,
        ...recordRest
      }}
    >
      {children}
    </ResourceContext.Provider>
  )
}

// ── withResource HOC ───────────────────────────────────────────────────────────

type PropConfigFunctionProps = any

type PropConfigFunction<TConfig extends Config = Config> = (
  props: PropConfigFunctionProps
) => TConfig

export function withResource<
  TRecord extends RecordType = RecordType,
  TConfig extends Config = Config
>(
  Component: React.ComponentType<any>,
  propConfig: TConfig | PropConfigFunction<TConfig>
) {
  const WrappedComponent = (props: PropConfigFunctionProps) => {
    const config = isFunction(propConfig) ? propConfig(props) : propConfig

    return (
      <ResourceProvider<TRecord> id={props?.id} config={config}>
        <Component {...props} />
      </ResourceProvider>
    )
  }

  WrappedComponent.displayName = `withResource(${
    Component.displayName || Component.name
  })`

  return WrappedComponent
}
